import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { $api } from '../api/queryClient'
import { apiClient } from '../api/client'
import type { components } from '../api/schema'
import { useCan } from '../contexts/PermissionsContext'
import { useLocale } from '../contexts/LocaleContext'
import { getLocalizedName } from '../lib/localization'
import { useFormatters } from '../lib/format'
import { apiErrorDetail } from '../lib/apiError'
import { Alert, AlertDescription } from './ui/alert'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Input } from './ui/input'
import { Label } from './ui/label'

type Contract = components['schemas']['Contract']
type NoticeOptions = components['schemas']['NoticeOptions']
type NoticeCandidate = components['schemas']['NoticeCandidate']
type GivenBy = 'tenant' | 'landlord'

const today = () => new Date().toISOString().slice(0, 10)

/** Refetch everything a notice moves: the lease, its options, and the availability of its objects. */
function useRefreshAfterNotice() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({
    predicate: (q) => {
      const [method, path] = q.queryKey as [string, string]
      return method === 'get' && typeof path === 'string' && (
        path.startsWith('/api/v1/organizations/{orgCode}/contracts') ||
        path === '/api/v1/organizations/{orgCode}/availability' ||
        path.startsWith('/api/v1/organizations/{orgCode}/assets/{assetUid}')
      )
    },
  })
}

function useNoticeOptions(org: string, contractUid: string, givenBy: GivenBy, receivedOn: string, enabled: boolean) {
  return $api.useQuery('get', '/api/v1/organizations/{orgCode}/contracts/{contractUid}/notice-options', {
    params: { path: { orgCode: org, contractUid }, query: { given_by: givenBy, received_on: receivedOn } },
  }, { enabled: enabled && /^\d{4}-\d{2}-\d{2}$/.test(receivedOn), retry: false })
}

function RulePeriod({ c }: { c: NoticeCandidate }) {
  const { t } = useTranslation('contracts')
  return (
    <span className="text-xs text-muted-foreground">
      {c.period_unit && <>{t(`lease_exit.period_${c.period_unit}`, { count: c.period_value })} </>}{t(`lease_exit.anchor_${c.anchor}`)}
      {c.legal_basis && <> · {c.legal_basis}</>}
    </span>
  )
}

/**
 * The Exit card of a lease (spec 2026-10-25-06 step 8): the notice, the end
 * the pack rule proposes and the manager's override. No period is computed
 * here: every rule and date comes from `notice-options`.
 */
export function ContractLeaseExitCard({ org, contract }: { org: string; contract: Contract }) {
  const { t } = useTranslation('contracts')
  const { locale } = useLocale()
  const { formatDate } = useFormatters()
  const canWrite = useCan('contracts.write')
  const refresh = useRefreshAfterNotice()
  const contractUid = contract.uid as string
  const recorded = !!contract.notice_received_on

  const [editing, setEditing] = useState(false)
  const [receivedOn, setReceivedOn] = useState(contract.notice_received_on ?? today())
  const [givenBy, setGivenBy] = useState<GivenBy>((contract.notice_given_by as GivenBy) ?? 'tenant')
  const [ruleUid, setRuleUid] = useState<string | null>(null)
  const [override, setOverride] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmClear, setConfirmClear] = useState(false)

  // The recorded notice's own options, to name its rule and spot an override.
  const { data: recordedOptions } = useNoticeOptions(org, contractUid, (contract.notice_given_by as GivenBy) ?? 'tenant',
    contract.notice_received_on ?? '', recorded && !editing)
  const { data: formOptions } = useNoticeOptions(org, contractUid, givenBy, receivedOn, editing)
  const options = formOptions as NoticeOptions | undefined

  useEffect(() => {
    if (!options) return
    const keep = ruleUid && options.candidates.some((c) => c.rule_uid === ruleUid)
    if (!keep) setRuleUid(options.default_rule_uid ?? null)
  }, [options]) // eslint-disable-line react-hooks/exhaustive-deps

  const chosen = useMemo(() => options?.candidates.find((c) => c.rule_uid === ruleUid), [options, ruleUid])
  const recordedRule = (recordedOptions as NoticeOptions | undefined)?.candidates.find((c) => c.rule_uid === contract.notice_rule_uid)
  const overridden = recorded && !!contract.ended_on && recordedRule?.proposed_ended_on !== contract.ended_on
  const needsManual = !!options && (options.status === 'not_configured' || !chosen?.proposed_ended_on)

  const open = () => {
    setReceivedOn(contract.notice_received_on ?? today())
    setGivenBy((contract.notice_given_by as GivenBy) ?? 'tenant')
    setRuleUid(contract.notice_rule_uid ?? null)
    setOverride(overridden ? contract.ended_on ?? '' : '')
    setError(null)
    setEditing(true)
  }

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      const { error: err } = await apiClient.PUT('/api/v1/organizations/{orgCode}/contracts/{contractUid}/notice', {
        params: { path: { orgCode: org, contractUid } },
        body: {
          notice_received_on: receivedOn,
          notice_given_by: givenBy,
          ...(chosen ? { notice_rule_uid: chosen.rule_uid } : {}),
          ...(override ? { ended_on: override } : {}),
        },
      })
      if (err) {
        setError(apiErrorDetail(err, t('lease_exit.save')))
        return
      }
      await refresh()
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  const clear = async () => {
    setConfirmClear(false)
    await apiClient.DELETE('/api/v1/organizations/{orgCode}/contracts/{contractUid}/notice', {
      params: { path: { orgCode: org, contractUid } },
    })
    await refresh()
  }

  return (
    <Card data-testid="lease-exit-card">
      <CardHeader className="flex flex-row items-center justify-between gap-3 pb-2">
        <CardTitle className="text-base">{t('lease_exit.title')}</CardTitle>
        {canWrite && recorded && !editing && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={open} data-testid="lease-exit-edit">{t('lease_exit.edit')}</Button>
            <Button variant="outline" size="sm" onClick={() => setConfirmClear(true)} data-testid="lease-exit-clear">{t('lease_exit.clear')}</Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {!editing && !recorded && (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-muted-foreground" data-testid="lease-exit-no-notice">{t('lease_exit.no_notice')}</span>
            {canWrite && (
              <Button size="sm" onClick={open} data-testid="lease-exit-record-notice">{t('lease_exit.record_notice')}</Button>
            )}
          </div>
        )}

        {!editing && recorded && (
          <dl className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2" data-testid="lease-exit-recorded">
            <div><dt className="text-xs text-muted-foreground">{t('lease_exit.received_on')}</dt>
              <dd data-testid="lease-exit-received-on-value">{formatDate(contract.notice_received_on as string)}</dd></div>
            <div><dt className="text-xs text-muted-foreground">{t('lease_exit.given_by')}</dt>
              <dd data-testid="lease-exit-given-by-value">{t(`lease_exit.given_by_${contract.notice_given_by}`)}</dd></div>
            <div><dt className="text-xs text-muted-foreground">{t('lease_exit.rule')}</dt>
              <dd data-testid="lease-exit-rule-value">{recordedRule ? getLocalizedName(recordedRule.label, locale) : t('lease_exit.manual_rule')}</dd></div>
            <div><dt className="text-xs text-muted-foreground">{t('lease_exit.ended_on')}</dt>
              <dd className="flex items-center gap-2">
                <span data-testid="lease-exit-ended-on" data-date={contract.ended_on ?? ''}>{contract.ended_on ? formatDate(contract.ended_on) : '—'}</span>
                {overridden && <Badge variant="outline" data-testid="lease-exit-override-badge">{t('lease_exit.override_badge')}</Badge>}
              </dd></div>
          </dl>
        )}

        {editing && (
          <div className="space-y-3" data-testid="lease-exit-form">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="lease-exit-received-on">{t('lease_exit.received_on')}</Label>
                <Input id="lease-exit-received-on" type="date" value={receivedOn} max={today()}
                  onChange={(e) => setReceivedOn(e.target.value)} data-testid="lease-exit-received-on" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="lease-exit-given-by">{t('lease_exit.given_by')}</Label>
                <select id="lease-exit-given-by" value={givenBy} onChange={(e) => setGivenBy(e.target.value as GivenBy)}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="lease-exit-given-by">
                  <option value="tenant">{t('lease_exit.given_by_tenant')}</option>
                  <option value="landlord">{t('lease_exit.given_by_landlord')}</option>
                </select>
              </div>
            </div>

            {options?.status === 'not_configured' && (
              <Alert data-testid="lease-exit-not-configured" data-reason={options.reason}>
                <AlertDescription>{t(`lease_exit.not_configured.${options.reason}`)} {t('lease_exit.ended_on_required')}</AlertDescription>
              </Alert>
            )}
            {options && options.candidates.length > 0 && (
              <fieldset className="space-y-1" data-testid="lease-exit-rules">
                <legend className="text-xs text-muted-foreground">{t('lease_exit.rules')}</legend>
                {options.candidates.map((c) => (
                  <label key={c.rule_uid} className="flex cursor-pointer items-start gap-2 rounded-md border border-border p-2">
                    <input type="radio" name="lease-exit-rule" className="mt-1" checked={ruleUid === c.rule_uid}
                      onChange={() => setRuleUid(c.rule_uid)} data-testid={`lease-exit-rule-${c.code}`} />
                    <span className="flex flex-col">
                      <span>{getLocalizedName(c.label, locale)}</span>
                      <RulePeriod c={c} />
                    </span>
                  </label>
                ))}
              </fieldset>
            )}
            {chosen?.warning === 'late_for_term' && (
              <Alert variant="destructive" data-testid="lease-exit-late-warning">
                <AlertDescription>{t('lease_exit.late_warning')}</AlertDescription>
              </Alert>
            )}
            {options?.status === 'configured' && (
              <div data-testid="lease-exit-proposed-end" data-date={chosen?.proposed_ended_on ?? ''}>
                <span className="text-xs text-muted-foreground">{t('lease_exit.proposed_end')}: </span>
                <span className="font-medium">{chosen?.proposed_ended_on ? formatDate(chosen.proposed_ended_on) : t('lease_exit.no_proposed_end')}</span>
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor="lease-exit-ended-on-override">{t('lease_exit.ended_on_override')}</Label>
              <Input id="lease-exit-ended-on-override" type="date" value={override} required={needsManual}
                onChange={(e) => setOverride(e.target.value)} className="w-48" data-testid="lease-exit-ended-on-override" />
              <p className="text-xs text-muted-foreground">{needsManual ? t('lease_exit.ended_on_required') : t('lease_exit.ended_on_override_help')}</p>
            </div>
            {error !== null && (
              <Alert variant="destructive" data-testid="lease-exit-error">
                <AlertDescription>{t('lease_exit.error', { detail: error })}</AlertDescription>
              </Alert>
            )}
            <div className="flex gap-2">
              <Button size="sm" onClick={() => void save()} disabled={saving || !receivedOn || (needsManual && !override)}
                data-testid="lease-exit-save">{t('lease_exit.save')}</Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(false)} data-testid="lease-exit-cancel">{t('lease_exit.cancel')}</Button>
            </div>
          </div>
        )}
      </CardContent>

      <AlertDialog open={confirmClear} onOpenChange={setConfirmClear}>
        <AlertDialogContent data-testid="lease-exit-clear-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('lease_exit.clear_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('lease_exit.clear_description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lease-exit-clear-cancel">{t('lease_exit.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => void clear()} data-testid="lease-exit-clear-confirm">{t('lease_exit.clear_confirm')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
